import { useAuthStore } from '../stores/authStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// ========================================
// 型定義
// ========================================

interface BlockCheckResult {
  isBlocked: boolean
  blockInfo?: BlockInfo | null
}

interface BlockInfo {
  blockId: string
  phoneNumber: string
  reason: string
  notes?: string
  isBlocked: boolean
  createdAt?: string
  expiresAt?: number
}

interface BlockListResult {
  blocks: BlockInfo[]
  lastKey?: string
  total: number
}

interface BlockLogEntry {
  logId: string
  phoneNumber: string
  blockedAt: string
  reason?: string
}

interface BlockedCallLogsResult {
  logs: BlockLogEntry[]
  lastKey?: string
  total: number
}

interface BlockListApiResponse {
  blocks?: BlockInfo[]
  block?: BlockInfo
  lastKey?: string
  total?: number
}

interface BlockedCallLogsApiResponse {
  logs?: BlockLogEntry[]
  lastKey?: string
  total?: number
}

interface CallHistoryForBlock {
  callHistoryId?: string
  callId?: string
  from?: string
  phoneNumber?: string
}

// ========================================
// ヘルパー関数
// ========================================

// 認証ヘッダーを取得するヘルパー関数
const getAuthHeaders = (): Record<string, string> => {
  const state = useAuthStore.getState()
  const token = state.getAccessToken()

  if (!token) {
    throw new Error('認証トークンが見つかりません')
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }
}

// APIリクエストのヘルパー関数
const apiRequest = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const headers: Record<string, string> = {
    ...getAuthHeaders(),
    ...(options.headers as Record<string, string>),
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  return response.json() as Promise<T>
}

// ========================================
// エクスポート関数
// ========================================

/**
 * 電話番号のブロック状態をチェック
 */
export async function checkBlockStatus(phoneNumber: string): Promise<BlockCheckResult> {
  try {
    if (!phoneNumber) return { isBlocked: false, blockInfo: null }

    const data = await apiRequest<BlockCheckResult>(`/api/blocklist/check/${encodeURIComponent(phoneNumber)}`)
    return data
  } catch (error) {
    console.error('ブロック状態チェックエラー:', error)
    return { isBlocked: false, blockInfo: null }
  }
}

/**
 * 電話番号のブロック詳細情報を取得
 */
export async function getBlockInfo(phoneNumber: string): Promise<BlockCheckResult | null> {
  try {
    if (!phoneNumber) return null

    const data = await apiRequest<BlockCheckResult>(`/api/blocklist/check/${encodeURIComponent(phoneNumber)}`)
    return data.isBlocked ? data : null
  } catch (error) {
    console.error('ブロック情報取得エラー:', error)
    return null
  }
}

/**
 * 電話番号をブロックリストに追加
 */
export async function quickBlockNumber(phoneNumber: string, reason = '迷惑電話', notes = '', expiresAt: number | null = null): Promise<BlockInfo | undefined> {
  try {
    const payload: { phoneNumber: string; reason: string; notes: string; expiresAt?: number } = {
      phoneNumber,
      reason,
      notes,
    }

    if (expiresAt) {
      payload.expiresAt = expiresAt
    }

    const data = await apiRequest<BlockListApiResponse>('/api/blocklist', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return data.block
  } catch (error) {
    console.error('ブロック追加エラー:', error)
    throw error
  }
}

/**
 * ブロックリストから削除
 */
export async function unblockNumber(blockId: string): Promise<boolean> {
  try {
    await apiRequest(`/api/blocklist/${blockId}`, {
      method: 'DELETE',
    })
    return true
  } catch (error) {
    console.error('ブロック解除エラー:', error)
    throw error
  }
}

/**
 * ブロックリスト一覧を取得
 */
export async function getBlockList(limit = 50, lastKey: string | null = null): Promise<BlockListResult> {
  try {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (lastKey) {
      params.append('lastKey', lastKey)
    }

    const data = await apiRequest<BlockListApiResponse>(`/api/blocklist?${params.toString()}`)
    return {
      blocks: data.blocks || [],
      lastKey: data.lastKey,
      total: data.total || 0,
    }
  } catch (error) {
    console.error('ブロックリスト取得エラー:', error)
    throw error
  }
}

/**
 * ブロックログ一覧を取得
 */
export async function getBlockedCallLogs(limit = 50, lastKey: string | null = null): Promise<BlockedCallLogsResult> {
  try {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (lastKey) {
      params.append('lastKey', lastKey)
    }

    const data = await apiRequest<BlockedCallLogsApiResponse>(`/api/blocklist/logs?${params.toString()}`)
    return {
      logs: data.logs || [],
      lastKey: data.lastKey,
      total: data.total || 0,
    }
  } catch (error) {
    console.error('ブロックログ取得エラー:', error)
    throw error
  }
}

/**
 * 通話履歴からワンクリックでブロック
 */
export async function blockFromCallHistory(call: CallHistoryForBlock, additionalNotes = '', reason = '迷惑電話'): Promise<BlockInfo | undefined> {
  const notes = `通話履歴から追加 (通話ID: ${call.callHistoryId || call.callId})${additionalNotes ? '\n' + additionalNotes : ''}`

  return await quickBlockNumber(
    (call.from || call.phoneNumber)!,
    reason,
    notes
  )
}

/**
 * 通話メモからブロック（通話メモの内容を含める）
 */
export async function blockFromCallMemo(phoneNumber: string, callMemo = '', reason = '迷惑電話'): Promise<BlockInfo | undefined> {
  const notes = callMemo ? `通話メモ: ${callMemo}` : ''

  return await quickBlockNumber(phoneNumber, reason, notes)
}
